import React from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TextArea,
  TextInput,
  UnorderedList,
  MultiSelect,
  DatePicker,
  DatePickerInput,
} from "carbon-components-react";
import {
  Bankruptcy,
  BankruptcyOptions,
  BankruptcyType,
  BankruptcyTypeKeys,
  Citizenship,
  CitizenshipStatuses,
  Declaration as DDeclaration,
  NoteCokmakerStatuses,
  NoteComaker,
  PartyToLawsuit,
  PartyToLawsuitPaymentTypes,
  PropertyOwnershipInterest,
  PropertyOwnershipUsages,
  PropertyTitleOwnerships,
  PropertyUsages,
} from "@eave/shared/types/api";
import { EMDASH } from "underwriter_dashboard/hurdle_editors/AssetSummary/shared";
import { DeclarationError } from "underwriter_dashboard/hurdle_editors/Declarations/index";
import * as moment from "moment";
import { getBorrowerEntityAsString } from "@eave/shared/utils/borrowers";

const borrowerDeclarationStyles = require("./declarations.module.scss");

interface BorrowerDeclarationProps {
  borrowerName: string;
  borrowerEntity: number;
  declaration: DDeclaration;
  setDeclarationValue: Function;
  save: Function;
  resetScreen: Function;
  isEditing: boolean;
  validationErrors: DeclarationError[];
}

const propertyUsageOptions = [
  { text: "As my primary home", value: PropertyUsages.primary },
  { text: "As a second home or vacation home", value: PropertyUsages.secondary },
  { text: "As an investment property", value: PropertyUsages.investment },
  { text: "Neither of these", value: PropertyUsages.neither },
];

const getPropertyUsageOption = (usage: PropertyUsages | null): object => {
  if (usage == null) {
    return null;
  }
  if (usage === PropertyUsages.primary) {
    return propertyUsageOptions[0];
  }
  if (usage === PropertyUsages.secondary) {
    return propertyUsageOptions[1];
  }

  if (usage === PropertyUsages.investment) {
    return propertyUsageOptions[2];
  }

  return propertyUsageOptions[3];
};

const propertyOwnershipUsageOptions = [
  { text: "Principle Residence", value: PropertyOwnershipUsages.principle },
  { text: "Second Home", value: PropertyOwnershipUsages.secondHome },
  { text: "Investment Property", value: PropertyOwnershipUsages.investment },
];

const getPropertyOwnershipUsageOption = (
  ownershipInterest: PropertyOwnershipInterest | null,
): object => {
  if (ownershipInterest.usage == null) {
    return null;
  }
  if (ownershipInterest.usage === PropertyOwnershipUsages.principle) {
    return { text: "Principle Residence", value: PropertyOwnershipUsages.principle };
  }
  if (ownershipInterest.usage === PropertyOwnershipUsages.secondHome) {
    return { text: "Second Home", value: PropertyOwnershipUsages.secondHome };
  }
  if (ownershipInterest.usage === PropertyOwnershipUsages.investment) {
    return { text: "Investment Property", value: PropertyOwnershipUsages.investment };
  }
  return null;
};
const titleOwnershipOptions = [
  { text: "Solely By Yourself", value: PropertyTitleOwnerships.solely },
  { text: "Jointly with your spouse", value: PropertyTitleOwnerships.jointlyWithPartner },
  { text: "Jointly with another person", value: PropertyTitleOwnerships.jointlyWithOther },
];

const getTitleOwnershipOption = (ownershipInterest: PropertyOwnershipInterest | null): object => {
  if (ownershipInterest.titleOwnership == null) {
    return null;
  }
  if (ownershipInterest.titleOwnership === PropertyTitleOwnerships.solely) {
    return { text: "Solely By Yourself", value: PropertyTitleOwnerships.solely };
  }
  if (ownershipInterest.titleOwnership === PropertyTitleOwnerships.jointlyWithPartner) {
    return { text: "Jointly with your spouse", value: PropertyTitleOwnerships.jointlyWithPartner };
  }
  if (ownershipInterest.titleOwnership === PropertyTitleOwnerships.jointlyWithOther) {
    return { text: "Jointly with another person", value: PropertyTitleOwnerships.jointlyWithOther };
  }
  return null;
};

const citizenshipOptions = [
  { text: "U.S. Citizen", value: CitizenshipStatuses.usCitizen },
  { text: "Permanent Resident", value: CitizenshipStatuses.permanentResident },
  { text: "Non-Permanent Resident", value: CitizenshipStatuses.nonPermanentResident },
  { text: "None of these", value: CitizenshipStatuses.other },
];

const getCitizenshipOption = (citizenship: Citizenship | null): object => {
  if (citizenship == null || citizenship.status == null) {
    return null;
  }
  if (citizenship.status === CitizenshipStatuses.usCitizen) {
    return { text: "U.S. Citizen", value: CitizenshipStatuses.usCitizen };
  }
  if (citizenship.status === CitizenshipStatuses.permanentResident) {
    return { text: "Permanent Resident", value: CitizenshipStatuses.permanentResident };
  }
  if (citizenship.status === CitizenshipStatuses.nonPermanentResident) {
    return { text: "Non-Permanent Resident", value: CitizenshipStatuses.nonPermanentResident };
  }
  return { text: "None of these", value: CitizenshipStatuses.other };
};

const citizenshipStatusesRequiringConfirmation = ["non_pr_alien", "none"];

const noteComakerStatusOptions = [
  { text: "Current", value: NoteCokmakerStatuses.current },
  { text: "Past Due", value: NoteCokmakerStatuses.pastDue },
  { text: "Defaulted", value: NoteCokmakerStatuses.defaulted },
];

const getNoteComakerStatusOption = (noteComaker: NoteComaker | null): object | null => {
  if (noteComaker == null || noteComaker.status == null) {
    return null;
  }
  if (noteComaker.status === NoteCokmakerStatuses.current) {
    return { text: "Current", value: NoteCokmakerStatuses.current };
  }
  if (noteComaker.status === NoteCokmakerStatuses.pastDue) {
    return { text: "Past Due", value: NoteCokmakerStatuses.pastDue };
  }
  if (noteComaker.status === NoteCokmakerStatuses.defaulted) {
    return { text: "Defaulted", value: NoteCokmakerStatuses.defaulted };
  }
  return null;
};

const bankruptcyTypeOptions = BankruptcyOptions.map(bankruptcyOption => {
  return { text: bankruptcyOption.label, value: bankruptcyOption.value };
});

const getBankruptcyTypeOptions = (bankruptcy: Bankruptcy | null): any[] | null => {
  const options = [];
  if (bankruptcy == null || bankruptcy.bankruptcyType == null) {
    return options;
  }

  bankruptcy.bankruptcyType.forEach((bankruptcyType: BankruptcyTypeKeys) => {
    if (bankruptcyType === BankruptcyTypeKeys.ChapterSeven) {
      options.push({ text: BankruptcyType.ChapterSeven, value: BankruptcyTypeKeys.ChapterSeven });
    } else if (bankruptcyType === BankruptcyTypeKeys.ChapterEleven) {
      options.push({ text: BankruptcyType.ChapterEleven, value: BankruptcyTypeKeys.ChapterEleven });
    } else if (bankruptcyType === BankruptcyTypeKeys.ChapterTwelve) {
      options.push({ text: BankruptcyType.ChapterTwelve, value: BankruptcyTypeKeys.ChapterTwelve });
    } else if (bankruptcyType === BankruptcyTypeKeys.ChapterThirteen) {
      options.push({
        text: BankruptcyType.ChapterThirteen,
        value: BankruptcyTypeKeys.ChapterThirteen,
      });
    }
    // For declarations with a noted bankruptcy created before the new field to capture bankruptcy type the bankruptcy
    // object will have a single bankruptcy type of "". This will ignore the empty bankruptcy type when displaying the
    // selected types
  });

  return options;
};

const getBankruptcyLabel = (bankruptcy: Bankruptcy) => {
  const selected = getBankruptcyTypeOptions(bankruptcy).map(bankruptcyType => bankruptcyType.text);
  if (selected.length === 0) {
    return "Select One or More Types";
  }
  return selected.join(", ");
};

const partyToLawsuitPaymentTypeOptions = [
  { text: "Once", value: PartyToLawsuitPaymentTypes.onceOff },
  { text: "Monthly", value: PartyToLawsuitPaymentTypes.monthly },
];

const getPartyToLawsuitPaymentTypeOption = (
  partyToLawsuit: PartyToLawsuit | null,
): object | null => {
  if (partyToLawsuit == null || partyToLawsuit.paymentType == null) {
    return null;
  }
  if (partyToLawsuit.paymentType === PartyToLawsuitPaymentTypes.onceOff) {
    return { text: "Once", value: PartyToLawsuitPaymentTypes.onceOff };
  }
  if (partyToLawsuit.paymentType === PartyToLawsuitPaymentTypes.monthly) {
    return { text: "Monthly", value: PartyToLawsuitPaymentTypes.monthly };
  }
  return null;
};

const isFalse = (value?: boolean) => {
  return value === false;
};

const BorrowerDeclaration = (props: BorrowerDeclarationProps) => {
  const {
    borrowerName,
    borrowerEntity,
    declaration,
    setDeclarationValue,
    save,
    resetScreen,
    isEditing,
    validationErrors,
  } = props;

  if (!declaration) {
    return <h2>No Declarations Exist for {borrowerName}</h2>;
  }

  const fieldIsInvalid = (fieldName: string): boolean => {
    const error = validationErrors.find(err => err.fieldInError === fieldName) || undefined;
    return error !== undefined;
  };

  const fieldErrorMessage = (fieldName: string): string => {
    const validationErrorIdx =
      Object.keys(validationErrors).find(idx => validationErrors[idx].fieldInError === fieldName) ||
      undefined;
    return validationErrorIdx === undefined
      ? ""
      : validationErrors[validationErrorIdx].errorMessage;
  };

  const booleanQuestion = (
    letter: string,
    text: string,
    key: string,
    value: boolean,
    nullable: boolean = true,
  ) => {
    const rowKey = `row-${key}-${borrowerEntity}`;
    return (
      <TableRow>
        <TableCell className={borrowerDeclarationStyles.letterCell}>{letter}</TableCell>
        <TableCell>{text}</TableCell>
        <TableCell>
          {fieldIsInvalid(key) && (
            <span className={borrowerDeclarationStyles.declarationsFieldError}>
              {fieldErrorMessage(key)}
            </span>
          )}
          <Checkbox
            id={`${rowKey}-is-true-checkbox`}
            labelText="Yes"
            checked={value}
            onChange={val => {
              if (nullable) {
                return val
                  ? setDeclarationValue(borrowerEntity, key, true)
                  : setDeclarationValue(borrowerEntity, key, null);
              }
              return setDeclarationValue(borrowerEntity, key, val);
            }}
          />
        </TableCell>
        <TableCell>
          <Checkbox
            id={`${rowKey}-is-false-checkbox`}
            labelText="No"
            checked={isFalse(value)}
            onChange={val => {
              if (nullable) {
                return val
                  ? setDeclarationValue(borrowerEntity, key, false)
                  : setDeclarationValue(borrowerEntity, key, null);
              }
              return setDeclarationValue(borrowerEntity, key, !val);
            }}
          />
        </TableCell>
      </TableRow>
    );
  };

  const setObjectDeclarationValue = (isTrue: boolean, key: string) => {
    if (isTrue) {
      setDeclarationValue(borrowerEntity, key, {});
    } else {
      setDeclarationValue(borrowerEntity, key, null);
    }
  };

  const cancel = () => {
    resetScreen(borrowerEntity);
  };

  return (
    <Form className={borrowerDeclarationStyles.declarationsForm}>
      <TableContainer title={`${getBorrowerEntityAsString(borrowerEntity)} - ${borrowerName}`}>
        <TableToolbar>
          <TableToolbarContent>
            {isEditing && (
              <Button tabIndex={0} onClick={() => cancel()} size="small" kind="secondary">
                Cancel
              </Button>
            )}
            {isEditing && (
              <Button tabIndex={0} onClick={() => save(borrowerEntity)} size="small" kind="primary">
                Save
              </Button>
            )}
          </TableToolbarContent>
        </TableToolbar>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader />
              <TableHeader key="Declaration">Declaration</TableHeader>
              <TableHeader key="Response" colSpan={2}>
                Response
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>{EMDASH}</TableCell>
              <TableCell>What is your Citizenship Status?</TableCell>
              <TableCell colSpan={2}>
                <Dropdown
                  id={`citizenship-${borrowerEntity}-selection`}
                  label="" // Carbon makes this a required field for some reason
                  helperText="Select Citizenship Status"
                  items={citizenshipOptions}
                  itemToString={item => (item ? item.text : EMDASH)}
                  onChange={e =>
                    setDeclarationValue(
                      borrowerEntity,
                      "citizenship",
                      e.selectedItem.value,
                      "status",
                    )
                  }
                  selectedItem={getCitizenshipOption(declaration.citizenship)}
                  invalid={fieldIsInvalid("citizenship.status")}
                  invalidText={fieldErrorMessage("citizenship.status")}
                />
              </TableCell>
            </TableRow>
            {declaration.citizenship &&
              declaration.citizenship.status === CitizenshipStatuses.other && (
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    Please provide details:
                  </TableCell>
                  <TableCell colSpan={2}>
                    <TextArea
                      id={`citizenship-${borrowerEntity}-explanation`}
                      labelText="Explanation"
                      value={declaration.citizenship.explanation}
                      onChange={e =>
                        setDeclarationValue(
                          borrowerEntity,
                          "citizenship",
                          e.target.value,
                          "explanation",
                        )
                      }
                      type="text"
                      invalid={fieldIsInvalid("citizenship.explanation")}
                      invalidText={fieldErrorMessage("citizenship.explanation")}
                    />
                  </TableCell>
                </TableRow>
              )}
            {declaration.citizenship &&
              citizenshipStatusesRequiringConfirmation.includes(declaration.citizenship.status) && (
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    <UnorderedList>
                      <ListItem>
                        I confirm that my current visa category is among the following: H1B, H1C,
                        H4, G1, G2, G3, G4, E1, E2, E3, L1A, L1B, L2, P1, R1, TN, O-1
                      </ListItem>
                      <ListItem>
                        I confirm that I have been living and working in the US for at least 2 years
                      </ListItem>
                      <ListItem>I confirm that I have a valid Social Security Number</ListItem>
                      <ListItem>
                        I confirm that I have filed individual federal tax returns in the US for the
                        last 2 years
                      </ListItem>
                    </UnorderedList>
                  </TableCell>
                  <TableCell>
                    {fieldIsInvalid("citizenship.confirmation") && (
                      <span className={borrowerDeclarationStyles.declarationsFieldError}>
                        {fieldErrorMessage("citizenship.confirmation")}
                      </span>
                    )}
                    <Checkbox
                      id={`citizenship-confirmation-${borrowerEntity}-is-true-checkbox`}
                      labelText="Yes"
                      checked={
                        declaration.citizenship.confirmation != null &&
                        declaration.citizenship.confirmation
                      }
                      onChange={val =>
                        val
                          ? setDeclarationValue(borrowerEntity, "citizenship", true, "confirmation")
                          : setDeclarationValue(borrowerEntity, "citizenship", null, "confirmation")
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      id={`citizenship-confirmation-${borrowerEntity}-is-false-checkbox`}
                      labelText="No"
                      checked={isFalse(declaration.citizenship.confirmation)}
                      onChange={val =>
                        val
                          ? setDeclarationValue(
                              borrowerEntity,
                              "citizenship",
                              false,
                              "confirmation",
                            )
                          : setDeclarationValue(borrowerEntity, "citizenship", null, "confirmation")
                      }
                    />
                  </TableCell>
                </TableRow>
              )}

            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>A.</TableCell>
              <TableCell>How do you intend to use this property?</TableCell>
              <TableCell colSpan={2}>
                <Dropdown
                  id={`propertyUsage-${borrowerEntity}-selection`}
                  label="" // Carbon makes this a required field for some reason
                  helperText="Property Usage"
                  items={propertyUsageOptions}
                  itemToString={item => (item ? item.text : EMDASH)}
                  onChange={e =>
                    setDeclarationValue(borrowerEntity, "propertyUsage", e.selectedItem.value)
                  }
                  selectedItem={getPropertyUsageOption(declaration.propertyUsage)}
                  invalid={fieldIsInvalid("propertyUsage")}
                  invalidText={fieldErrorMessage("propertyUsage")}
                />
              </TableCell>
            </TableRow>
            {declaration.propertyUsage && declaration.propertyUsage === PropertyUsages.primary && (
              <TableRow>
                <TableCell />
                <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                  I have had an ownership interest in a property in the last three years.
                </TableCell>
                <TableCell>
                  {fieldIsInvalid("propertyOwnershipInterest") && (
                    <span className={borrowerDeclarationStyles.declarationsFieldError}>
                      {fieldErrorMessage("propertyOwnershipInterest")}
                    </span>
                  )}
                  <Checkbox
                    id={`propertyOwnershipInterest-${borrowerEntity}-is-true-checkbox`}
                    labelText="Yes"
                    checked={declaration.propertyOwnershipInterest != null}
                    onChange={val => setObjectDeclarationValue(val, "propertyOwnershipInterest")}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    id={`propertyOwnershipInterest-${borrowerEntity}-is-false-checkbox`}
                    labelText="No"
                    checked={declaration.propertyOwnershipInterest == null}
                    onChange={val => setObjectDeclarationValue(!val, "propertyOwnershipInterest")}
                  />
                </TableCell>
              </TableRow>
            )}
            {declaration.propertyUsage &&
              declaration.propertyUsage === PropertyUsages.primary &&
              declaration.propertyOwnershipInterest && (
                <>
                  <TableRow>
                    <TableCell />
                    <TableCell className={borrowerDeclarationStyles.subSubQuestionCell}>
                      1. What type of property did you own?
                    </TableCell>
                    <TableCell colSpan={2}>
                      <Dropdown
                        id={`propertyOwnershipInterest-${borrowerEntity}-usage-selection`}
                        label="" // Carbon makes this a required field for some reason
                        helperText="Property Type"
                        items={propertyOwnershipUsageOptions}
                        itemToString={item => (item ? item.text : EMDASH)}
                        onChange={e =>
                          setDeclarationValue(
                            borrowerEntity,
                            "propertyOwnershipInterest",
                            e.selectedItem.value,
                            "usage",
                          )
                        }
                        selectedItem={getPropertyOwnershipUsageOption(
                          declaration.propertyOwnershipInterest,
                        )}
                        invalid={fieldIsInvalid("propertyOwnershipInterest.usage")}
                        invalidText={fieldErrorMessage("propertyOwnershipInterest.usage")}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell />
                    <TableCell className={borrowerDeclarationStyles.subSubQuestionCell}>
                      2. How did you hold title to the home?
                    </TableCell>
                    <TableCell colSpan={2}>
                      <Dropdown
                        id={`propertyOwnershipInterest-${borrowerEntity}-titleOwnership-selection`}
                        label="" // Carbon makes this a required field for some reason
                        helperText="Title Ownership"
                        items={titleOwnershipOptions}
                        itemToString={item => (item ? item.text : EMDASH)}
                        onChange={e =>
                          setDeclarationValue(
                            borrowerEntity,
                            "propertyOwnershipInterest",
                            e.selectedItem.value,
                            "titleOwnership",
                          )
                        }
                        selectedItem={getTitleOwnershipOption(
                          declaration.propertyOwnershipInterest,
                        )}
                        invalid={fieldIsInvalid("propertyOwnershipInterest.titleOwnership")}
                        invalidText={fieldErrorMessage("propertyOwnershipInterest.titleOwnership")}
                      />
                    </TableCell>
                  </TableRow>
                </>
              )}

            {booleanQuestion(
              "B.",
              "I have a family relationship or business affiliation with the seller of the property.",
              "affiliatedWithSeller",
              declaration.affiliatedWithSeller,
            )}

            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>C.</TableCell>
              <TableCell>Part of the downpayment is being borrowed?</TableCell>
              <TableCell>
                {fieldIsInvalid("borrowedDownpayment") && (
                  <span className={borrowerDeclarationStyles.declarationsFieldError}>
                    {fieldErrorMessage("borrowedDownpayment")}
                  </span>
                )}
                <Checkbox
                  id={`borrowedDownpayment-${borrowerEntity}-is-true-checkbox`}
                  labelText="Yes"
                  checked={declaration.borrowedDownpayment != null}
                  onChange={val => setObjectDeclarationValue(val, "borrowedDownpayment")}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  id={`borrowedDownpayment-${borrowerEntity}-is-false-checkbox`}
                  labelText="No"
                  checked={declaration.borrowedDownpayment == null}
                  onChange={val => setObjectDeclarationValue(!val, "borrowedDownpayment")}
                />
              </TableCell>
            </TableRow>
            {declaration.borrowedDownpayment && (
              <TableRow>
                <TableCell />
                <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                  Please enter the amount being borrowed:
                </TableCell>
                <TableCell colSpan={2}>
                  <TextInput
                    id={`borrowedDownpayment-${borrowerEntity}-is-true-amount`}
                    labelText="Borrowed Amount"
                    value={declaration.borrowedDownpayment.amount}
                    onChange={e =>
                      setDeclarationValue(
                        borrowerEntity,
                        "borrowedDownpayment",
                        e.target.value,
                        "amount",
                      )
                    }
                    type="number"
                    invalid={fieldIsInvalid("borrowedDownpayment.amount")}
                    invalidText={fieldErrorMessage("borrowedDownpayment.amount")}
                  />
                </TableCell>
              </TableRow>
            )}

            {booleanQuestion(
              "D.",
              "1. I have or will be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application.",
              "applyingForMortgageOnOther",
              declaration.applyingForMortgageOnOther,
            )}

            {booleanQuestion(
              "",
              "2. I have or will be applying for any new credit (e.g. installment loan, credit card, etc.) on or before closing this loan that is not disclosed on this loan application.",
              "applyingForCredit",
              declaration.applyingForCredit,
            )}

            {booleanQuestion(
              "E.",
              "This property will be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through my property taxes (e.g., the Property Assessed Clean Energy Program).",
              "propertySubjectToALien",
              declaration.propertySubjectToALien,
            )}

            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>F.</TableCell>
              <TableCell>
                I am a co-signer or guarantor on any debt or loan that is not disclosed on this
                application.
              </TableCell>
              <TableCell>
                {fieldIsInvalid("noteComaker") && (
                  <span className={borrowerDeclarationStyles.declarationsFieldError}>
                    {fieldErrorMessage("noteComaker")}
                  </span>
                )}
                <Checkbox
                  id={`noteComaker-${borrowerEntity}-is-true-checkbox`}
                  labelText="Yes"
                  checked={declaration.noteComaker != null}
                  onChange={val => setObjectDeclarationValue(val, "noteComaker")}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  id={`noteComaker-${borrowerEntity}-is-false-checkbox`}
                  labelText="No"
                  checked={declaration.noteComaker == null}
                  onChange={val => setObjectDeclarationValue(!val, "noteComaker")}
                />
              </TableCell>
            </TableRow>
            {declaration.noteComaker && (
              <>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    What is the balance due on the note or debt?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <TextInput
                      id={`noteComaker-${borrowerEntity}-is-true-balanceDue`}
                      labelText="Balance Due"
                      value={declaration.noteComaker.balanceDue}
                      onChange={e =>
                        setDeclarationValue(
                          borrowerEntity,
                          "noteComaker",
                          e.target.value,
                          "balanceDue",
                        )
                      }
                      type="number"
                      invalid={fieldIsInvalid("noteComaker.balanceDue")}
                      invalidText={fieldErrorMessage("noteComaker.balanceDue")}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    What is the current status of the note or debt?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <Dropdown
                      id={`noteComaker-${borrowerEntity}-selection`}
                      label="" // Carbon makes this a required field for some reason
                      helperText="Select A Status"
                      items={noteComakerStatusOptions}
                      itemToString={item => (item ? item.text : EMDASH)}
                      onChange={e =>
                        setDeclarationValue(
                          borrowerEntity,
                          "noteComaker",
                          e.selectedItem.value,
                          "status",
                        )
                      }
                      selectedItem={getNoteComakerStatusOption(declaration.noteComaker)}
                      invalid={fieldIsInvalid("noteComaker.status")}
                      invalidText={fieldErrorMessage("noteComaker.status")}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    What is the monthly payment on the note or debt?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <TextInput
                      id={`noteComaker-${borrowerEntity}-is-true-monthlyPayment`}
                      labelText="Monthly Payment"
                      value={declaration.noteComaker.monthlyPayment}
                      onChange={e =>
                        setDeclarationValue(
                          borrowerEntity,
                          "noteComaker",
                          e.target.value,
                          "monthlyPayment",
                        )
                      }
                      type="number"
                      invalid={fieldIsInvalid("noteComaker.monthlyPayment")}
                      invalidText={fieldErrorMessage("noteComaker.monthlyPayment")}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    What is the date of first payment?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <DatePicker
                      id={`noteComaker-${borrowerEntity}-is-true-firstPaymentDate`}
                      datePickerType="single"
                      dateFormat="Y-m-d"
                      style={{ marginBottom: "10px" }}
                      onChange={e => {
                        const val = e[0] !== null ? moment(e[0]).format("YYYY-MM-DD") : null;
                        setDeclarationValue(borrowerEntity, "noteComaker", val, "firstPaymentDate");
                      }}
                      value={declaration.noteComaker.firstPaymentDate || ""}
                    >
                      <DatePickerInput
                        id={`noteComaker-${borrowerEntity}-is-true-firstPaymentDate-input`}
                        iconDescription="Select a date value"
                        labelText="First Payment Date:"
                        placeholder="yyyy-mm-dd"
                        type="text"
                        size="sm"
                        invalid={fieldIsInvalid("noteComaker.firstPaymentDate")}
                        invalidText={fieldErrorMessage("noteComaker.firstPaymentDate")}
                      />
                    </DatePicker>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    What is the date of your most recent payment?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <DatePicker
                      id={`noteComaker-${borrowerEntity}-is-true-mostRecentPaymentDate`}
                      datePickerType="single"
                      dateFormat="Y-m-d"
                      style={{ marginBottom: "10px" }}
                      onChange={e => {
                        const val = e[0] !== null ? moment(e[0]).format("YYYY-MM-DD") : null;
                        setDeclarationValue(
                          borrowerEntity,
                          "noteComaker",
                          val,
                          "mostRecentPaymentDate",
                        );
                      }}
                      value={declaration.noteComaker.mostRecentPaymentDate || ""}
                    >
                      <DatePickerInput
                        id={`noteComaker-${borrowerEntity}-is-true-mostRecentPaymentDate-input`}
                        iconDescription="Select a date value"
                        labelText="Most Recent Payment Date:"
                        placeholder="yyyy-mm-dd"
                        type="text"
                        size="sm"
                        invalid={fieldIsInvalid("noteComaker.mostRecentPaymentDate")}
                        invalidText={fieldErrorMessage("noteComaker.mostRecentPaymentDate")}
                      />
                    </DatePicker>
                  </TableCell>
                </TableRow>
              </>
            )}

            {booleanQuestion(
              "G.",
              "I have had outstanding judgements against me as a result of a lawsuit.",
              "outstandingJudgments",
              declaration.outstandingJudgments,
              false,
            )}

            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>H.</TableCell>
              <TableCell>I am currently delinquent or in default on a Federal debt.</TableCell>
              <TableCell>
                {fieldIsInvalid("delinquentOrDefaulted") && (
                  <span className={borrowerDeclarationStyles.declarationsFieldError}>
                    {fieldErrorMessage("delinquentOrDefaulted")}
                  </span>
                )}
                <Checkbox
                  id={`delinquentOrDefaulted-${borrowerEntity}-is-true-checkbox`}
                  labelText="Yes"
                  checked={declaration.delinquentOrDefaulted != null}
                  onChange={val => setObjectDeclarationValue(val, "delinquentOrDefaulted")}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  id={`delinquentOrDefaulted-${borrowerEntity}-is-false-checkbox`}
                  labelText="No"
                  checked={declaration.delinquentOrDefaulted == null}
                  onChange={val => setObjectDeclarationValue(!val, "delinquentOrDefaulted")}
                />
              </TableCell>
            </TableRow>
            {declaration.delinquentOrDefaulted && (
              <TableRow>
                <TableCell />
                <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                  Please provide details:
                </TableCell>
                <TableCell colSpan={2}>
                  <TextInput
                    id={`delinquentOrDefaulted-${borrowerEntity}-is-true-details`}
                    labelText="Explanation"
                    value={declaration.delinquentOrDefaulted.explanation}
                    onChange={e =>
                      setDeclarationValue(
                        borrowerEntity,
                        "delinquentOrDefaulted",
                        e.target.value,
                        "explanation",
                      )
                    }
                    type="text"
                    invalid={fieldIsInvalid("delinquentOrDefaulted.explanation")}
                    invalidText={fieldErrorMessage("delinquentOrDefaulted.explanation")}
                  />
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>I.</TableCell>
              <TableCell>
                I am party to a lawsuit in which I potentially have a personal financial liability.
              </TableCell>
              <TableCell>
                {fieldIsInvalid("partyToLawsuit") && (
                  <span className={borrowerDeclarationStyles.declarationsFieldError}>
                    {fieldErrorMessage("partyToLawsuit")}
                  </span>
                )}
                <Checkbox
                  id={`partyToLawsuit-${borrowerEntity}-is-true-checkbox`}
                  labelText="Yes"
                  checked={declaration.partyToLawsuit != null}
                  onChange={val => setObjectDeclarationValue(val, "partyToLawsuit")}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  id={`partyToLawsuit-${borrowerEntity}-is-false-checkbox`}
                  labelText="No"
                  checked={declaration.partyToLawsuit == null}
                  onChange={val => setObjectDeclarationValue(!val, "partyToLawsuit")}
                />
              </TableCell>
            </TableRow>
            {declaration.partyToLawsuit && (
              <>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    Have you received a ruling?
                  </TableCell>
                  <TableCell>
                    {fieldIsInvalid("partyToLawsuit.rulingReceived") && (
                      <span className={borrowerDeclarationStyles.declarationsFieldError}>
                        {fieldErrorMessage("partyToLawsuit.rulingReceived")}
                      </span>
                    )}
                    <Checkbox
                      id={`partyToLawsuit-${borrowerEntity}-is-true-ruling-true`}
                      labelText="Yes"
                      checked={declaration.partyToLawsuit.rulingReceived}
                      onChange={val =>
                        setDeclarationValue(borrowerEntity, "partyToLawsuit", val, "rulingReceived")
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      id={`partyToLawsuit-${borrowerEntity}-is-true-ruling-false`}
                      labelText="No"
                      checked={isFalse(declaration.partyToLawsuit.rulingReceived)}
                      onChange={val =>
                        setDeclarationValue(
                          borrowerEntity,
                          "partyToLawsuit",
                          !val,
                          "rulingReceived",
                        )
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    {
                      "If, unfortunately, things don't go in your favor, do you expect to pay the judgement all at once or on a monthly basis?"
                    }
                  </TableCell>
                  <TableCell colSpan={2}>
                    <Dropdown
                      id={`partyToLawsuit-${borrowerEntity}-paymentType`}
                      label="" // Carbon makes this a required field for some reason
                      helperText="Payment Type"
                      items={partyToLawsuitPaymentTypeOptions}
                      itemToString={item => (item ? item.text : EMDASH)}
                      onChange={e =>
                        setDeclarationValue(
                          borrowerEntity,
                          "partyToLawsuit",
                          e.selectedItem.value,
                          "paymentType",
                        )
                      }
                      selectedItem={getPartyToLawsuitPaymentTypeOption(declaration.partyToLawsuit)}
                      invalid={fieldIsInvalid("partyToLawsuit.paymentType")}
                      invalidText={fieldErrorMessage("partyToLawsuit.paymentType")}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    How much do you expect to pay?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <TextInput
                      id={`partyToLawsuit-${borrowerEntity}-paymentAmount`}
                      labelText="Payment Amount"
                      value={declaration.partyToLawsuit.paymentAmount}
                      onChange={e =>
                        setDeclarationValue(
                          borrowerEntity,
                          "partyToLawsuit",
                          e.target.value,
                          "paymentAmount",
                        )
                      }
                      type="number"
                      invalid={fieldIsInvalid("partyToLawsuit.paymentAmount")}
                      invalidText={fieldErrorMessage("partyToLawsuit.paymentAmount")}
                    />
                  </TableCell>
                </TableRow>
              </>
            )}

            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>J.</TableCell>
              <TableCell>
                I have conveyed title to any property in lieu of foreclosure in the past 7 years.
              </TableCell>
              <TableCell>
                {fieldIsInvalid("loanResultingInForeclosure") && (
                  <span className={borrowerDeclarationStyles.declarationsFieldError}>
                    {fieldErrorMessage("loanResultingInForeclosure")}
                  </span>
                )}
                <Checkbox
                  id={`loanResultingInForeclosure-${borrowerEntity}-is-true-checkbox`}
                  labelText="Yes"
                  checked={declaration.loanResultingInForeclosure != null}
                  onChange={val => setObjectDeclarationValue(val, "loanResultingInForeclosure")}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  id={`loanResultingInForeclosure-${borrowerEntity}-is-false-checkbox`}
                  labelText="No"
                  checked={declaration.loanResultingInForeclosure == null}
                  onChange={val => setObjectDeclarationValue(!val, "loanResultingInForeclosure")}
                />
              </TableCell>
            </TableRow>
            {declaration.loanResultingInForeclosure && (
              <TableRow>
                <TableCell />
                <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                  Please provide details:
                </TableCell>
                <TableCell colSpan={2}>
                  <TextInput
                    id={`loanResultingInForeclosure-${borrowerEntity}-is-true-details`}
                    labelText="Explanation"
                    value={declaration.loanResultingInForeclosure.explanation}
                    onChange={e =>
                      setDeclarationValue(
                        borrowerEntity,
                        "loanResultingInForeclosure",
                        e.target.value,
                        "explanation",
                      )
                    }
                    type="text"
                    invalid={fieldIsInvalid("loanResultingInForeclosure.explanation")}
                    invalidText={fieldErrorMessage("loanResultingInForeclosure.explanation")}
                  />
                </TableCell>
              </TableRow>
            )}

            {booleanQuestion(
              "K.",
              "Within the past 7 years, I have completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due.",
              "preForeclosureSaleInPastSevenYears",
              declaration.preForeclosureSaleInPastSevenYears,
            )}

            {booleanQuestion(
              "L.",
              "I have had property foreclosed upon in the last 7 years.",
              "foreclosedOrGivenTitle",
              declaration.foreclosedOrGivenTitle,
              false,
            )}

            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>M.</TableCell>
              <TableCell>I have declared bankruptcy within the past 7 years.</TableCell>
              <TableCell>
                {fieldIsInvalid("bankruptcy") && (
                  <span className={borrowerDeclarationStyles.declarationsFieldError}>
                    {fieldErrorMessage("bankruptcy")}
                  </span>
                )}
                <Checkbox
                  id={`bankruptcy-${borrowerEntity}-is-true-checkbox`}
                  labelText="Yes"
                  checked={declaration.bankruptcy != null}
                  onChange={val => setObjectDeclarationValue(val, "bankruptcy")}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  id={`bankruptcy-${borrowerEntity}-is-false-checkbox`}
                  labelText="No"
                  checked={declaration.bankruptcy == null}
                  onChange={val => setObjectDeclarationValue(!val, "bankruptcy")}
                />
              </TableCell>
            </TableRow>
            {declaration.bankruptcy && (
              <TableRow>
                <TableCell />
                <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                  Please identify the type(s) of bankruptcy:
                </TableCell>
                <TableCell colSpan={2}>
                  <MultiSelect
                    id={`bankruptcy-${borrowerEntity}-types`}
                    label={getBankruptcyLabel(declaration.bankruptcy)}
                    items={bankruptcyTypeOptions}
                    itemToString={item => (item ? item.text : EMDASH)}
                    onChange={e =>
                      setDeclarationValue(
                        borrowerEntity,
                        "bankruptcy",
                        e.selectedItems.map(item => item.value),
                        "bankruptcyType",
                      )
                    }
                    initialSelectedItems={getBankruptcyTypeOptions(declaration.bankruptcy)}
                    invalid={fieldIsInvalid("bankruptcy.bankruptcyType")}
                    invalidText={fieldErrorMessage("bankruptcy.bankruptcyType")}
                  />
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell className={borrowerDeclarationStyles.letterCell}>{EMDASH}</TableCell>
              <TableCell>
                I am obligated to pay alimony, child support, or separate maintenance.
              </TableCell>
              <TableCell>
                {fieldIsInvalid("alimony") && (
                  <span className={borrowerDeclarationStyles.declarationsFieldError}>
                    {fieldErrorMessage("alimony")}
                  </span>
                )}
                <Checkbox
                  id={`alimony-${borrowerEntity}-is-true-checkbox`}
                  labelText="Yes"
                  checked={declaration.alimony != null}
                  onChange={val => setObjectDeclarationValue(val, "alimony")}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  id={`alimony-${borrowerEntity}-is-false-checkbox`}
                  labelText="No"
                  checked={declaration.alimony == null}
                  onChange={val => setObjectDeclarationValue(!val, "alimony")}
                />
              </TableCell>
            </TableRow>
            {declaration.alimony && (
              <>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    What is the monthly amount of alimony, child support, or other obligation?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <TextInput
                      id={`alimony-${borrowerEntity}-is-true-amount`}
                      labelText="Monthly Amount"
                      value={declaration.alimony.monthlyPayment}
                      onChange={e =>
                        setDeclarationValue(
                          borrowerEntity,
                          "alimony",
                          e.target.value,
                          "monthlyPayment",
                        )
                      }
                      type="number"
                      invalid={fieldIsInvalid("alimony.monthlyPayment")}
                      invalidText={fieldErrorMessage("alimony.monthlyPayment")}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    What is the date of first payment?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <DatePicker
                      id={`alimony-${borrowerEntity}-is-true-firstPaymentDate`}
                      datePickerType="single"
                      dateFormat="Y-m-d"
                      style={{ marginBottom: "10px" }}
                      onChange={e => {
                        const val = e[0] !== null ? moment(e[0]).format("YYYY-MM-DD") : null;
                        setDeclarationValue(borrowerEntity, "alimony", val, "firstPaymentDate");
                      }}
                      value={declaration.alimony.firstPaymentDate || ""}
                    >
                      <DatePickerInput
                        id={`alimony-${borrowerEntity}-is-true-firstPaymentDate-input`}
                        iconDescription="Select a date value"
                        labelText="First Payment Date:"
                        placeholder="yyyy-mm-dd"
                        type="text"
                        size="sm"
                        invalid={fieldIsInvalid("alimony.firstPaymentDate")}
                        invalidText={fieldErrorMessage("alimony.firstPaymentDate")}
                      />
                    </DatePicker>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell className={borrowerDeclarationStyles.subQuestionCell}>
                    What is the date of your most recent payment?
                  </TableCell>
                  <TableCell colSpan={2}>
                    <DatePicker
                      id={`alimony-${borrowerEntity}-is-true-mostRecentPaymentDate`}
                      datePickerType="single"
                      dateFormat="Y-m-d"
                      style={{ marginBottom: "10px" }}
                      onChange={e => {
                        const val = e[0] !== null ? moment(e[0]).format("YYYY-MM-DD") : null;
                        setDeclarationValue(
                          borrowerEntity,
                          "alimony",
                          val,
                          "mostRecentPaymentDate",
                        );
                      }}
                      value={declaration.alimony.mostRecentPaymentDate || ""}
                    >
                      <DatePickerInput
                        id={`alimony-${borrowerEntity}-is-true-mostRecentPaymentDate-input`}
                        iconDescription="Select a date value"
                        labelText="Most Recent Payment Date:"
                        placeholder="yyyy-mm-dd"
                        type="text"
                        size="sm"
                        invalid={fieldIsInvalid("alimony.mostRecentPaymentDate")}
                        invalidText={fieldErrorMessage("alimony.mostRecentPaymentDate")}
                      />
                    </DatePicker>
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Form>
  );
};

export default BorrowerDeclaration;
